import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  useToggleInstagramChatbot,
  useTogglePageChatbot,
} from '@/features/integrations/api';
import { Facebook, Instagram } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { IntegrationSettingsDialog } from './integration-settings-dialog';

interface ConnectedProfile {
  name: string | null;
  email?: string | null;
  pictureUrl: string | null;
}

interface ConnectedPage {
  id: string;
  name: string;
  platform: 'facebook' | 'instagram';
  isDefault: boolean;
}

/**
 * Read-only view of the Meta connection. The pages and ad account are chosen
 * in Meta's own login flow and there is no endpoint that changes them from
 * here, so they are shown rather than offered as choices. The Instagram
 * chatbot switch is the one control, and it saves as soon as it is flipped.
 */
interface FacebookSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisconnect?: () => void;
  pages: ConnectedPage[];
  adAccountName: string | null;
  facebookProfile?: ConnectedProfile | null;
  /**
   * Present when the combined "Facebook & Instagram" card is active and an
   * Instagram account is linked to the connected Page. Surfaces the linked IG
   * identity + its OWN (per-channel) chatbot toggle, independent of Messenger.
   */
  instagram?: {
    username: string | null;
    name: string | null;
    chatbotEnabled: boolean;
    /** Set when the account has no standalone connection: toggle the Page's Instagram flag. */
    pageRowId?: string;
  } | null;
}

export function FacebookSettingsDialog({
  open,
  onOpenChange,
  onDisconnect,
  pages,
  adAccountName,
  facebookProfile,
  instagram,
}: FacebookSettingsDialogProps) {
  const pagesLabelId = useId();
  const [igChatbotEnabled, setIgChatbotEnabled] = useState(
    instagram?.chatbotEnabled ?? false
  );
  useEffect(() => {
    setIgChatbotEnabled(instagram?.chatbotEnabled ?? false);
  }, [instagram?.chatbotEnabled]);

  // A standalone connection carries the flag on instagram_integration; a
  // Page-linked-only account carries it on the Page (`<page id>:ig`). The two
  // endpoints keep each other in step when both exist.
  const { toggleInstagramChatbot, isToggling: isTogglingStandaloneIg } =
    useToggleInstagramChatbot();
  const { togglePageChatbot, isToggling: isTogglingPageIg } =
    useTogglePageChatbot();
  const isTogglingIg = isTogglingStandaloneIg || isTogglingPageIg;

  const handleToggleIgChatbot = (checked: boolean) => {
    setIgChatbotEnabled(checked); // optimistic flip
    if (instagram?.pageRowId) {
      togglePageChatbot(
        { pageId: instagram.pageRowId, enabled: checked },
        { onError: () => setIgChatbotEnabled(!checked) }
      );
      return;
    }
    toggleInstagramChatbot(
      { enabled: checked },
      {
        onSuccess: () => {
          setIgChatbotEnabled(checked);
          toast.success(
            checked ? 'Instagram chatbot enabled' : 'Instagram chatbot disabled'
          );
        },
        onError: (error: Error) => {
          setIgChatbotEnabled(!checked); // revert optimistic flip
          toast.error(error.message || 'Failed to update Instagram chatbot');
        },
      }
    );
  };

  return (
    <IntegrationSettingsDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Facebook"
      icon={<img src="/fb-icon.svg" alt="Facebook" className="size-10" />}
      onSave={() => onOpenChange(false)}
      saveLabel="Done"
      saveIcon={null}
      onDisconnect={onDisconnect}
    >
      {/* Connected Profile */}
      {facebookProfile?.name && (
        <div className="space-y-3 rounded-lg border p-3">
          <div className="flex items-center gap-3">
            <Avatar className="size-9">
              {facebookProfile.pictureUrl && (
                <AvatarImage
                  src={facebookProfile.pictureUrl}
                  alt={facebookProfile.name}
                />
              )}
              <AvatarFallback>
                <Facebook className="size-4" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {facebookProfile.name}
              </p>
              {facebookProfile.email && (
                <p className="truncate text-xs text-muted-foreground">
                  {facebookProfile.email}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <p id={pagesLabelId} className="text-sm font-medium">
          Connected pages
        </p>
        {pages.length > 0 ? (
          <ul
            aria-labelledby={pagesLabelId}
            className="divide-y rounded-lg border"
          >
            {pages.map((page) => (
              <li
                key={page.id}
                className="flex items-center gap-2 px-3 py-2 text-sm"
              >
                {page.platform === 'instagram' ? (
                  <Instagram className="size-4 shrink-0" aria-hidden />
                ) : (
                  <Facebook className="size-4 shrink-0" aria-hidden />
                )}
                <span className="min-w-0 flex-1 truncate">{page.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {page.platform === 'instagram' ? 'Instagram' : 'Facebook'}
                </span>
                {page.isDefault && <Badge variant="secondary">Default</Badge>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No pages connected</p>
        )}
      </div>

      <div className="space-y-1">
        <p className="text-sm font-medium">Ad account</p>
        <p className="text-sm text-muted-foreground">
          {adAccountName ?? 'No ad account connected'}
        </p>
      </div>

      <p className="text-xs text-muted-foreground">
        Pages and the ad account are chosen when you connect Facebook. They
        can't be changed here.
      </p>

      {/* Instagram (linked to the connected Page) — per-channel controls, kept
          separate from Messenger so "disabled stays disabled". */}
      {instagram && (
        <div className="space-y-3 rounded-lg border p-3">
          <div className="flex items-center gap-3">
            <Avatar className="size-9">
              <AvatarFallback>
                <Instagram className="size-4" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {instagram.username
                  ? `@${instagram.username}`
                  : (instagram.name ?? 'Instagram')}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                Connected via your Facebook Page
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">Instagram chatbot</p>
              <p className="text-xs text-muted-foreground">
                Auto-reply to Instagram DMs. Independent of Messenger.
              </p>
            </div>
            <Switch
              checked={igChatbotEnabled}
              disabled={isTogglingIg}
              onCheckedChange={handleToggleIgChatbot}
              aria-label="Toggle Instagram chatbot"
            />
          </div>
        </div>
      )}
    </IntegrationSettingsDialog>
  );
}
